import {
  replyStatesSchema,
  supportRequestFieldsSchema,
} from "@go-novus/admin-api";
import { z } from "zod";

// 商品卡和来源标注一起存进快照：点商品卡是普通跳转，回来时整条回复要还在。
// 尺码卡、对比卡、加购回执同理；和商品卡一样按字段显式列写，只存挂件自己的视图字段。
const labeledValueSchema = z.object({ label: z.string(), value: z.string() });
const compareColumnSchema = z.object({
  title: z.string(),
  url: z.string().optional(),
});

export const conversationSnapshotSchema = z.object({
  open: z.boolean(),
  draft: z.string(),
  conversationId: z.string().optional(),
  historyCursor: z.string().optional(),
  pendingMessageId: z.string().optional(),
  /** 会话最近一次落在哪件商品上：换了商品才插系统提示，同一件来回看不重复插。 */
  contextProductId: z.string().optional(),
  supportEdits: z
    .record(
      z.string(),
      z.object({
        version: z.number().int().positive(),
        fields: supportRequestFieldsSchema.strip().safeExtend({
          contact_email: z.string(),
          description: z.string(),
          order_reference: z.string(),
        }),
      }),
    )
    .optional(),
  messages: z.array(
    z.object({
      id: z.string(),
      replyStates: replyStatesSchema.optional(),
      /** transfer 消息待补开卡；false 表示已确认过期，不再补建；不构成服务端授权。 */
      supportNeedsOpen: z.boolean().optional(),
      /** 本地发送标识与服务端消息标识分开，重试及历史补读可关联同一气泡。 */
      serverMessageId: z.string().optional(),
      role: z.enum(["user", "ai", "system"]),
      text: z.string(),
      textFormat: z.enum(["plain", "blocks"]).optional(),
      /** 没被后端收下：标记跟着消息走，换页回来气泡还是标着未送达。 */
      failed: z.boolean().optional(),
      source: z.string().optional(),
      products: z
        .array(
          // 快照按字段显式列写：放开成 looseObject 等于把后端回复原样写进买家浏览器。
          z.object({
            imageUrl: z.string().optional(),
            itemId: z.string().optional(),
            parentItemId: z.string().optional(),
            price: z.string(),
            title: z.string(),
            url: z.string().optional(),
            variantId: z.string().optional(),
          }),
        )
        .optional(),
      sizeCard: z
        .object({
          measurements: z.array(labeledValueSchema),
          recommendedSize: z.string(),
          source: z.string().optional(),
          usSize: z.string().optional(),
        })
        .optional(),
      cartReceipt: z
        .object({
          kind: z.enum(["added", "updated"]),
          title: z.string(),
          variant: z.string().optional(),
          imageUrl: z.string().optional(),
          variantId: z.number().int().positive(),
          lineKey: z.string().optional(),
          quantity: z.number().int().positive(),
          added: z.number().int().positive().optional(),
          requested: z.number().int().positive(),
        })
        .optional(),
      compareCard: z
        .object({
          columns: z.tuple([compareColumnSchema, compareColumnSchema]),
          rows: z.array(
            z.object({
              label: z.string(),
              values: z.tuple([z.string(), z.string()]),
            }),
          ),
        })
        .optional(),
    }),
  ),
  retry: z
    .object({
      text: z.string(),
      msgId: z.string(),
    })
    .optional(),
  /** 快捷回答：只回应最后那句 AI 的话，买家发出任何一句就收掉。 */
  quickReplies: z.array(z.string()).optional(),
  /** 聊天窗收起时到的回复：打开聊天窗或关掉回复提示才算看过，换页也保留。 */
  unreadReplyId: z.string().optional(),
});

export type ConversationSnapshot = z.infer<typeof conversationSnapshotSchema>;

/** Playground 每个 uid 一份快照；店面买家共用 "shopper"。 */
export function conversationStorageKey(
  shopDomain: string,
  playgroundUid?: string,
): string {
  return `gonovus:conversation:${shopDomain}:${playgroundUid ?? "shopper"}`;
}

/** sessionStorage 在隐私模式等环境可能直接抛错；读不到就只活在当前页。 */
export function readWidgetStorage():
  Pick<Storage, "getItem" | "setItem" | "removeItem"> | undefined {
  try {
    return sessionStorage;
  } catch {
    return undefined;
  }
}

/** 只读快照：入口在聊天资源到位前靠它判断要不要自动恢复、抑制主动提示。 */
export function readConversationSnapshot(
  storage: Pick<Storage, "getItem"> | undefined,
  key: string,
): ConversationSnapshot | undefined {
  try {
    const parsed = conversationSnapshotSchema.safeParse(
      JSON.parse(storage?.getItem(key) ?? "null") as unknown,
    );
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}
